import { useState } from "react";
import { Plus } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { useLanguage } from "../../../contexts/LanguageContext";
import { financialLabels } from "../labels";
import { api } from "../../../api";
import { toast } from "sonner";

export function AddExpenseDialog({ onSuccess }: { onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { lang } = useLanguage();
  const t = financialLabels[lang];
  
  const [formData, setFormData] = useState({
    description: "",
    amount_cents: "",
    category: "Fixo",
    due_date: new Date().toISOString().split('T')[0],
    status: 'pending' as 'pending' | 'paid',
    payment_method: 'Pix',
    is_recurring: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const amount = Math.floor(parseFloat(formData.amount_cents.replace(',', '.')) * 100);
    const res = await api.createExpense({
      ...formData,
      amount_cents: amount
    });
    setLoading(false);
    if (res.ok) {
      toast.success(lang === 'pt' ? "Despesa adicionada" : "Expense added");
      setOpen(false);
      onSuccess();
      setFormData({
        description: "",
        amount_cents: "",
        category: "Fixo",
        due_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        payment_method: 'Pix',
        is_recurring: false
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          className="h-9 font-bold shadow-sm px-4 bg-red-600 hover:bg-red-700 text-white border-none"
        >
          <Plus className="mr-2 h-4 w-4" />
          {lang === 'pt' ? "Nova Saída" : "New Expense"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{lang === 'pt' ? "Registrar Despesa" : "Register Expense"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="description">{lang === 'pt' ? "Descrição" : "Description"}</Label>
            <Input 
              id="description" 
              placeholder={lang === 'pt' ? "Ex: Aluguel, Luz, Fornecedor..." : "e.g. Rent, Electricity, Supplier..."} 
              required
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">{lang === 'pt' ? "Valor (R$)" : "Amount"}</Label>
              <Input 
                id="amount" 
                placeholder="0,00" 
                required
                value={formData.amount_cents}
                onChange={e => setFormData(prev => ({ ...prev, amount_cents: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">{lang === 'pt' ? "Categoria" : "Category"}</Label>
              <Select value={formData.category} onValueChange={v => setFormData(prev => ({ ...prev, category: v }))}>
                <SelectTrigger id="category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Fixo">{lang === 'pt' ? "Custo Fixo" : "Fixed Cost"}</SelectItem>
                  <SelectItem value="Variável">{lang === 'pt' ? "Custo Variável" : "Variable Cost"}</SelectItem>
                  <SelectItem value="Pessoal">{lang === 'pt' ? "Pessoal/Salários" : "Staff/Salaries"}</SelectItem>
                  <SelectItem value="Outros">{lang === 'pt' ? "Outros" : "Others"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="due_date">{lang === 'pt' ? "Vencimento" : "Due Date"}</Label>
              <Input 
                id="due_date" 
                type="date" 
                required
                value={formData.due_date}
                onChange={e => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="method">{lang === 'pt' ? "Método" : "Method"}</Label>
              <Select value={formData.payment_method} onValueChange={v => setFormData(prev => ({ ...prev, payment_method: v }))}>
                <SelectTrigger id="method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pix">Pix</SelectItem>
                  <SelectItem value="Cartão">{t.methods.card}</SelectItem>
                  <SelectItem value="Dinheiro">{t.methods.cash}</SelectItem>
                  <SelectItem value="Transferência">{t.methods.transfer}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input 
              type="checkbox" 
              id="recurring" 
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={formData.is_recurring}
              onChange={e => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
            />
            <Label htmlFor="recurring" className="text-xs font-medium cursor-pointer">
              {lang === 'pt' ? "Despesa recorrente (mensal)" : "Recurring expense (monthly)"}
            </Label>
          </div>
          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
              {lang === 'pt' ? "Cancelar" : "Cancel"}
            </Button>
            <Button type="submit" variant="destructive" disabled={loading} className="font-bold">
              {loading ? "..." : (lang === 'pt' ? "Salvar Saída" : "Save Expense")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
